import cn from 'classnames'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { getFormattedFinance } from 'cng-common/helpers/numbers'
import { LoaderOverlay } from 'core-components/library/loader'
import { useMeasureController } from 'core/analytics'
import { getKeyInitiativeStatusTranslationKey } from 'partner360/entities/keyInitiatives/model/status'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { getShortTermData } from '../../../../api/shortTermData/getShortTermData'
import { eltEventBus } from '../../../../eventBus/eltEventBus'
import { getFormedEltTranslation } from '../../../../helpers/getFormedEltTranslation'
import { IShortTermInitiative } from '../../../../models/IShortTermInitiative'
import { Title } from '../../../styledComponents/title/Title'

import { FINANCIAL_VALUE_FORMAT_OPTIONS } from './config'
import { getStatusClass } from './helpers/getStatusClass'
import { TRANSLATION_KEYS } from './translations'

import styles from './ShortTermInitiatives.module.scss'

interface IShortTermInitiativeItem extends IShortTermInitiative {
  isNewOffering?: boolean
  isParent?: boolean
  hasChildren?: boolean
  isChild?: boolean
}

interface IShortTermInitiativesProps {
  uid?: string
  netNdp: ActualsValueTypes
}

export const ShortTermInitiatives = ({ uid, netNdp }: IShortTermInitiativesProps) => {
  /** -------------------- State ----------------------- */

  const [isLoading, setIsLoading] = useState(false)
  const [shortTermData, setShortTermData] = useState<IShortTermInitiative[]>([])
  const [shortTermSorted, setShortTermSorted] = useState<IShortTermInitiativeItem[]>([])

  /** -------------------- Hooks ----------------------- */

  const { startLoadData, stopLoadData } = useMeasureController('ShortTermInitiatives')

  /** -------------------- Variables ----------------------- */

  const isNet = useMemo(() => {
    return netNdp === ActualsValueTypes.Net
  }, [netNdp])

  /** -------------------- Methods ----------------------- */

  const fetchShortTermData = useCallback(
    async (uid: string, forceUpdate: boolean = false) => {
      setIsLoading(true)
      startLoadData()

      const settingsResponse = await getShortTermData<IShortTermInitiative>(uid, forceUpdate)
      const data = settingsResponse?.Data || []

      setShortTermData(data)

      stopLoadData()
      setIsLoading(false)
    },
    [startLoadData, stopLoadData]
  )

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    if (uid) {
      fetchShortTermData(uid)
    }
  }, [fetchShortTermData, uid])

  useEffect(() => {
    const unsubscribe = eltEventBus.subscribe('updateScoreCardData', () => {
      if (uid) {
        fetchShortTermData(uid, true)
      }
    })

    return () => {
      unsubscribe()
    }
  }, [uid])

  useEffect(() => {
    if (shortTermData?.length) {
      const sortedData: IShortTermInitiativeItem[] = []

      const programTypes = shortTermData
        .sort((a, b) => (a.ProgramType ?? '').localeCompare(b.ProgramType))
        .map((el) => el.ProgramType)
      const uniqueProgramTypes = new Set(programTypes)

      let lastProgramType = ''
      uniqueProgramTypes.forEach((programType) => {
        const stsForOffering = shortTermData.filter((el) => el.ProgramType === programType)
        const keyObjectives = stsForOffering.map((el) => el.KeyObjective)
        const uniqueKeyObjectives = new Set(keyObjectives)

        uniqueKeyObjectives.forEach((keyObjective) => {
          const stsForRoot = stsForOffering.filter((el) => el.KeyObjective === keyObjective)
          let programType = ''
          if (stsForRoot.length) {
            programType = stsForRoot[0].ProgramType
          }

          let parentItem: IShortTermInitiativeItem = {
            KeyObjective: keyObjective,
            ProgramType: programType,
            isParent: true,
            hasChildren: !!stsForRoot.length,
          }
          if (lastProgramType !== programType) {
            lastProgramType = programType
            parentItem = { ...parentItem, isNewOffering: true }
          }
          sortedData.push({ ...parentItem })

          stsForRoot.forEach((item) => {
            sortedData.push({ ...item, isChild: true })
          })
        })
      })

      setShortTermSorted(sortedData)
    }
  }, [shortTermData])

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      <LoaderOverlay show={isLoading} />
      <Title
        text={getFormedEltTranslation('short-term-initiatives-table-title')}
        variant={'withBackground'}
      />
      <table className={styles.table}>
        {shortTermSorted.map((item, idx) => {
          const goal = isNet ? item.GoalNetUSD : item.GoalNdpUSD
          const goalStr = getFormattedFinance(goal, FINANCIAL_VALUE_FORMAT_OPTIONS)

          return (
            <Fragment key={`short-term-initiatives-table-row-${idx}`}>
              {!!item.isNewOffering && (
                <>
                  <tr>
                    <td colSpan={7}>&nbsp;</td>
                  </tr>
                  <tr className={styles.offeringRow}>
                    <td colSpan={7}>{item.ProgramType}</td>
                  </tr>
                  <tr className={styles.header}>
                    <th>
                      <Translation text={TRANSLATION_KEYS.tableColumns.name} span />
                    </th>
                    <th>
                      <Translation text={TRANSLATION_KEYS.tableColumns.description} span />
                    </th>
                    <th>
                      <Translation text={TRANSLATION_KEYS.tableColumns.businessUnit} span />
                    </th>
                    <th className={styles.numbers}>
                      <Translation text={TRANSLATION_KEYS.tableColumns.financialGoal} span />
                    </th>
                    <th>
                      <Translation text={TRANSLATION_KEYS.tableColumns.period} span />
                    </th>
                    <th>
                      <Translation text={TRANSLATION_KEYS.tableColumns.owner} span />
                    </th>
                    <th>
                      <Translation text={TRANSLATION_KEYS.tableColumns.status} span />
                    </th>
                  </tr>
                </>
              )}
              <tr className={cn(styles.dataRow, { [styles.rootRow]: !!item.isParent })}>
                {item.isParent ? (
                  <td colSpan={7}>
                    {!!item.isParent && !item.hasChildren && (
                      <i className={cn(styles.icon, 'kbase kbase-caret-right')} />
                    )}
                    {!!item.hasChildren && (
                      <i className={cn(styles.icon, styles.down, 'kbase kbase-caret-down')} />
                    )}
                    <span className={cn({ [styles.child]: !!item.isChild })}>
                      {item.KeyObjective}
                    </span>
                  </td>
                ) : (
                  <>
                    <td>{item.Name}</td>
                    <td>{item.Description}</td>
                    <td>{item.BusinessUnitTranslation}</td>
                    <td className={styles.numbers}>{goalStr}</td>
                    <td>{item.Period}</td>
                    <td>{item.Owner}</td>
                    <td>
                      <div className={cn(styles.status, getStatusClass(item.Status))}>
                        <Translation
                          text={getKeyInitiativeStatusTranslationKey(item.Status)}
                          span
                        />
                      </div>
                    </td>
                  </>
                )}
              </tr>
            </Fragment>
          )
        })}
      </table>
    </div>
  )
}
